import type { LegalCommon, LegalDocument } from "@/components/LegalPageLayout";

const legalEyebrow = "Правовая информация";
const updated = "Последнее обновление: 1 августа 2026 г.";

/**
 * Перевод legal-sl.ts. Юридически обязательной является словенская версия
 * (см. заключительные положения Условий); файл обновлять вслед за ней.
 */
export const legalRu = {
  common: {
    tocHeading: "Содержание",
    company: {
      legalName: "Наименование",
      address: "Юридический адрес",
      registrationNumber: "Регистрационный номер",
      vatNumber: "НДС-номер",
      taxNumber: "Налоговый номер",
      registryEntry: "Запись в реестре",
      shareCapital: "Уставный капитал",
      representative: "Представитель",
      email: "Эл. почта",
      phone: "Телефон",
      website: "Сайт",
    },
  } satisfies LegalCommon,

  terms: {
    eyebrow: legalEyebrow,
    title: "Общие условия ведения деятельности и использования",
    updated,
    intro:
      "Настоящие условия регулируют использование сайта nreport.ai и заказ отчётов о недвижимости. Сайт и сервис управляются компанией Noventis d.o.o. Пожалуйста, внимательно прочитайте условия — используя сервис, вы подтверждаете, что ознакомились с ними и принимаете их.",
    sections: [
      {
        title: "1. Сведения о поставщике услуг",
        blocks: [
          {
            type: "p",
            text: "Сайт nreport.ai и сервис nReport управляются и предоставляются компанией:",
          },
          { type: "company" },
          {
            type: "p",
            text: "Сведения публикуются в соответствии со статьёй 6 словенского Закона об электронной торговле (ZEPT) и статьёй 45 Закона о хозяйственных обществах (ZGD-1). Далее Noventis d.o.o. именуется «поставщик», «мы» или «nReport», а лицо, использующее сервис, — «пользователь» или «вы».",
          },
        ],
      },
      {
        title: "2. Сфера действия и принятие условий",
        blocks: [
          {
            type: "p",
            text: "Условия распространяются на всех посетителей сайта, зарегистрированных пользователей и покупателей отчётов — как потребителей, так и юридических лиц. Они являются неотъемлемой частью каждого договора, заключённого через сайт.",
          },
          {
            type: "p",
            text: "Вы принимаете условия при оформлении заказа. До завершения покупки условия всегда доступны вам в форме, которую можно сохранить и распечатать. Если вы не согласны с условиями, вы не вправе пользоваться сервисом.",
          },
          {
            type: "p",
            text: "Потребитель — физическое лицо, приобретающее или использующее услугу в целях, не связанных с его предпринимательской или профессиональной деятельностью. Положения, прямо обозначенные как потребительские, применяются только к потребителям; императивные нормы Закона о защите прав потребителей (ZVPot-1) действуют независимо от настоящих условий и не ограничиваются ими.",
          },
        ],
      },
      {
        title: "3. Описание услуги",
        blocks: [
          {
            type: "p",
            text: "nReport — онлайн-сервис, который для отдельного объекта недвижимости в Республике Словении автоматически собирает данные из общедоступных официальных реестров и опубликованных объявлений, обрабатывает их и формирует структурированный отчёт в виде документа PDF.",
          },
          { type: "p", text: "Отчёт, как правило, содержит:" },
          {
            type: "ul",
            items: [
              "данные кадастра недвижимости и реестра недвижимости (GURS),",
              "данные о совершённых сделках из Реестра рынка недвижимости (ETN),",
              "сведения о состоянии в поземельной книге, если выписка доступна,",
              "данные энергетического сертификата, если они имеются,",
              "обзор сопоставимых опубликованных объявлений,",
              "описание местоположения и окружения,",
              "ориентировочную оценку рыночной стоимости и текстовый анализ, подготовленный с помощью искусственного интеллекта.",
            ],
          },
          {
            type: "p",
            text: "Точное содержание зависит от доступности данных по конкретному объекту. Если источник недоступен или не содержит записи об объекте, соответствующая часть отчёта может быть сокращена или отсутствовать; это не является ненадлежащим исполнением.",
          },
        ],
      },
      {
        title: "4. Характер отчёта — чем он не является",
        blocks: [
          {
            type: "note",
            text: "Отчёт nReport — информационный вспомогательный материал. Он не является официальной оценкой и не является оценкой стоимости, выполненной по Закону об аудите (ZRev-2) и Международным стандартам оценки. Он не готовится сертифицированным оценщиком недвижимости и не может заменить его заключение.",
          },
          { type: "p", text: "Отчёт также не является:" },
          {
            type: "ul",
            items: [
              "юридической, налоговой, бухгалтерской или инвестиционной консультацией,",
              "заменой официальной выписки из поземельной книги или заверенной кадастровой выписки,",
              "заменой осмотра объекта на месте, технического обследования или юридической проверки,",
              "основанием для решения кредитора, страховщика или государственного органа,",
              "доказательством в судебном, налоговом или административном производстве.",
            ],
          },
          {
            type: "p",
            text: "Перед совершением сделки с недвижимостью рекомендуем получить официальные выписки из поземельной книги и кадастра и обратиться к адвокату, нотариусу, риелтору или сертифицированному оценщику.",
          },
        ],
      },
      {
        title: "5. Использование искусственного интеллекта",
        blocks: [
          {
            type: "p",
            text: "Текстовая часть отчёта и оценка стоимости формируются автоматически с помощью систем искусственного интеллекта (большие языковые модели) и статистических моделей. Как правило, содержание не проверяется человеком перед передачей.",
          },
          {
            type: "p",
            text: "Несмотря на тщательную настройку, системы ИИ могут выдавать неточные, неполные или вводящие в заблуждение утверждения, даже если исходные данные корректны. Любое утверждение отчёта, на которое вы намерены опираться, проверяйте в первоисточнике.",
          },
          {
            type: "p",
            text: "Мы не используем введённые вами в сервис данные и содержание подготовленных отчётов для обучения моделей ИИ и договорно запрещаем это поставщикам используемых нами моделей.",
          },
        ],
      },
      {
        title: "6. Источники данных и их актуальность",
        blocks: [
          {
            type: "p",
            text: "Отчёты основаны на общедоступных источниках, в том числе на реестрах Геодезического управления Республики Словении (кадастр недвижимости, реестр недвижимости, Реестр рынка недвижимости), поземельной книге, реестре энергетических сертификатов, картографических источниках и опубликованных объявлениях о недвижимости.",
          },
          {
            type: "p",
            text: "Поставщик не является источником этих данных и не влияет на их точность, полноту и актуальность. Официальные реестры обновляются с задержкой, а их содержание может отличаться от фактического состояния объекта (например, из-за незарегистрированных работ, несогласованных записей или ошибок в исходном реестре). Данные из объявлений отражают цену, запрашиваемую рекламодателем, а не достигнутую цену сделки.",
          },
          {
            type: "p",
            text: "Все данные в отчёте относятся к моменту его подготовки. После подготовки отчёт автоматически не обновляется.",
          },
        ],
      },
      {
        title: "7. Учётная запись пользователя",
        blocks: [
          {
            type: "ul",
            items: [
              "Для доступа к заказанным отчётам требуется учётная запись с действующим адресом электронной почты. Учётную запись можно создать с паролем либо через вход с Google или Facebook.",
              "При покупке без предварительной регистрации мы создаём учётную запись автоматически на указанный при оплате адрес электронной почты и направляем ссылку для входа.",
              "Предоставляемые вами сведения должны быть достоверными и актуальными.",
              "Вы самостоятельно отвечаете за сохранность пароля и за всё использование сервиса через вашу учётную запись. О подозрении на несанкционированное использование сообщите нам незамедлительно.",
              "Учётная запись предназначена для одного лица или одной организации; её передача и совместное использование не допускаются.",
              "Сервис не предназначен для лиц младше 18 лет.",
              "Вы можете удалить учётную запись в любое время; см. раздел об удалении данных в Политике конфиденциальности.",
            ],
          },
        ],
      },
      {
        title: "8. Кредиты, цены и оплата",
        blocks: [
          {
            type: "ul",
            items: [
              "Отчёты оплачиваются кредитами. Один кредит позволяет подготовить и открыть один полный отчёт по одному объекту.",
              "Цены опубликованы на сайте и указаны в евро (EUR). Указанная цена является окончательной ценой, которую вы платите. Поставщик не является плательщиком НДС, поэтому НДС не начисляется (пункт 1 статьи 94 закона ZDDV-1).",
              "Расходы на доставку отсутствуют, так как отчёт передаётся в электронной форме.",
              "Оплата производится авансом через поставщика платёжных услуг Stripe. Поставщик не получает и не хранит данные вашей платёжной карты.",
              "Счёт выставляется в электронной форме и направляется на ваш адрес электронной почты. Оформляя заказ, вы соглашаетесь на получение счёта в электронной форме.",
              "Приобретённые кредиты действительны в течение 1 года с даты покупки, как указано в прайс-листе. Неиспользованные кредиты после истечения срока действия не восстанавливаются, если не согласовано иное.",
              "Кредиты не подлежат выплате деньгами, кроме случаев, предусмотренных законом или настоящими условиями (например, при отказе от договора).",
            ],
          },
        ],
      },
      {
        title: "9. Заключение договора и исполнение",
        blocks: [
          {
            type: "p",
            text: "Представление услуги на сайте является приглашением делать оферты. Договор считается заключённым, когда вы оформляете заказ нажатием кнопки, прямо указывающей на обязанность оплаты, и оплата успешно подтверждена. Подтверждение заказа вместе с настоящими условиями на долговременном носителе направляется вам по электронной почте.",
          },
          {
            type: "p",
            text: "Подготовка отчёта начинается сразу после подтверждения оплаты либо сразу после списания кредита за выбранный объект. Отчёт обычно доступен в течение нескольких минут; если данные требуется получать из медленных или временно недоступных внешних реестров, подготовка может занять больше времени, но не более 48 часов. Отчёт доступен в вашей учётной записи, копия направляется также по электронной почте.",
          },
          {
            type: "p",
            text: "Если отчёт не может быть подготовлен по причинам на нашей стороне, мы возвращаем кредит на ваш счёт либо, по вашему требованию, возвращаем уплаченную сумму.",
          },
        ],
      },
      {
        title: "10. Право потребителя на отказ от договора",
        blocks: [
          {
            type: "p",
            text: "Согласно статье 134 ZVPot-1 потребитель вправе в течение 14 дней с момента заключения договора, заключённого дистанционным способом, отказаться от него без объяснения причин. Достаточно недвусмысленного заявления, направленного на info@nreport.ai; можно также использовать официальную словенскую форму отказа.",
          },
          {
            type: "p",
            text: "Применительно к покупке кредитов это означает: неиспользованные кредиты возвращаются в полном объёме в течение 14 дней без объяснения причин. Возврат производится тем же платёжным средством, не позднее 14 дней с момента получения заявления об отказе.",
          },
          {
            type: "note",
            text: "Исключение для уже подготовленного отчёта: согласно пункту 13 части 1 статьи 135 ZVPot-1 потребитель не имеет права на отказ в отношении цифрового содержания, поставляемого не на материальном носителе, если исполнение началось с прямого предварительного согласия потребителя и с его подтверждением того, что тем самым он утрачивает право на отказ. Поэтому мы запрашиваем это согласие прямо перед оплатой. После того как отчёт по выбранному объекту подготовлен и предоставлен вам, право на отказ в отношении израсходованного кредита отсутствует.",
          },
          {
            type: "p",
            text: "Если вы не даёте такого согласия, покупку через сайт совершить невозможно; в этом случае свяжитесь с нами по адресу info@nreport.ai, и мы согласуем иной порядок.",
          },
          {
            type: "p",
            text: "Право на отказ не распространяется на юридических лиц, индивидуальных предпринимателей и иных лиц, заказывающих услугу в рамках своей деятельности.",
          },
        ],
      },
      {
        title: "11. Соответствие цифрового содержания и требования по качеству",
        blocks: [
          {
            type: "p",
            text: "Мы обязуемся предоставить отчёт, соответствующий договору в смысле положений ZVPot-1 о цифровом содержании и цифровых услугах: он отвечает описанию в настоящих условиях и на сайте, пригоден для обычного назначения такого содержания и предоставляется в согласованном объёме.",
          },
          {
            type: "p",
            text: "Если отчёт не соответствует договору, потребитель вправе требовать приведения в соответствие (повторной подготовки или дополнения отчёта), снижения цены либо отказа от договора на условиях, установленных ZVPot-1. О несоответствии сообщите на info@nreport.ai с описанием недостатка. Мы отвечаем не позднее чем в течение 8 дней.",
          },
          {
            type: "p",
            text: "Расхождение данных официальных реестров с фактическим состоянием объекта не является несоответствием отчёта, поскольку поставщик эти данные не создаёт и за них не отвечает — см. разделы 6 и 12.",
          },
        ],
      },
      {
        title: "12. Ограничение ответственности",
        blocks: [
          {
            type: "p",
            text: "Сервис задуман как информационный вспомогательный инструмент. Поставщик не гарантирует точность, полноту и актуальность данных, полученных из внешних источников, а также точность оценок стоимости и текстовых анализов, созданных с помощью искусственного интеллекта.",
          },
          {
            type: "p",
            text: "Используя сервис, вы соглашаетесь не основывать решение о покупке, продаже, аренде, финансировании или оценке недвижимости исключительно на отчёте. Поставщик не несёт ответственности за деловые решения, принятые на основании отчёта, а также за упущенную выгоду, утраченные возможности и иной косвенный или последующий ущерб.",
          },
          {
            type: "p",
            text: "Совокупная ответственность поставщика по отдельному заказу ограничена суммой, уплаченной пользователем за этот заказ, если императивные нормы не предусматривают иного.",
          },
          {
            type: "note",
            text: "Ни одно положение настоящих условий не исключает и не ограничивает ответственность поставщика за умысел или грубую неосторожность, за вред жизни или здоровью, а также законные права потребителя по ZVPot-1 и Обязательственному кодексу.",
          },
          {
            type: "p",
            text: "Сервис предоставляется «как есть». Мы стремимся к бесперебойной работе, но не гарантируем постоянную доступность без ошибок и перерывов; плановые работы и сбои внешних источников данных ожидаемы.",
          },
        ],
      },
      {
        title: "13. Допустимое использование и запреты",
        blocks: [
          { type: "p", text: "При использовании сервиса запрещается:" },
          {
            type: "ul",
            items: [
              "систематически, автоматически или массово извлекать содержимое сайта (например, с помощью скрейпинга, ботов или интеллектуального анализа данных),",
              "пытаться получить доступ к частям системы, на которые у вас нет прав, или обходить механизмы безопасности,",
              "перегружать инфраструктуру или нарушать работу сервиса,",
              "использовать сервис для преследования, домогательств или профилирования лиц,",
              "заказывать отчёты по объектам, в отношении которых у вас нет законного интереса,",
              "перепродавать, распространять, публиковать отчёты или их части либо включать их в собственные продукты без нашего предварительного письменного разрешения,",
              "удалять или скрывать указания об авторстве, источниках данных и предупреждения в отчёте.",
            ],
          },
          {
            type: "p",
            text: "При нарушении мы вправе ограничить или приостановить доступ, а при грубых или повторных нарушениях — закрыть учётную запись. О принятой мере мы уведомляем и, по возможности, предоставляем возможность устранить нарушение.",
          },
        ],
      },
      {
        title: "14. Интеллектуальная собственность и лицензия на использование отчёта",
        blocks: [
          {
            type: "p",
            text: "Сайт, его дизайн, программный код, тексты, графика, товарный знак nReport, а также структура, отбор и представление данных в отчётах охраняются авторским правом и правом изготовителя базы данных (Закон об авторском и смежных правах). Все права защищены.",
          },
          {
            type: "p",
            text: "Приобретая отчёт, вы получаете неисключительное, непередаваемое и бессрочное право использовать отчёт для собственных нужд, включая предоставление его вашим консультантам, банку, риелтору или контрагенту по конкретной сделке. Любое более широкое использование, в частности публикация, перепродажа или систематическое воспроизведение, допускается только с нашего предварительного письменного разрешения.",
          },
          {
            type: "p",
            text: "Данные официальных публичных реестров принадлежат соответствующим органам или находятся в их ведении и используются в отчёте как сведения об общедоступном состоянии; права этих органов пользователю не передаются.",
          },
        ],
      },
      {
        title: "15. Передача отчёта по ссылке",
        blocks: [
          {
            type: "p",
            text: "Сервис позволяет создать ссылку для передачи вашего отчёта. Любой, кто знает ссылку, может просмотреть отчёт без входа в систему, поэтому делитесь ею обдуманно. Ссылку можно отозвать в любой момент в учётной записи; после отзыва отчёт по ней недоступен.",
          },
          {
            type: "p",
            text: "За передаваемое содержание и последствия передачи вы отвечаете самостоятельно.",
          },
        ],
      },
      {
        title: "16. Защита персональных данных",
        blocks: [
          {
            type: "p",
            text: "Мы обрабатываем персональные данные в соответствии с Общим регламентом по защите данных (GDPR) и Законом о защите персональных данных (ZVOP-2). Подробности о категориях данных, целях, правовых основаниях, сроках хранения, обработчиках и ваших правах изложены в Политике конфиденциальности, являющейся неотъемлемой частью настоящих условий.",
          },
        ],
      },
      {
        title: "17. Жалобы и внесудебное разрешение споров",
        blocks: [
          {
            type: "p",
            text: "Жалобу можно направить на info@nreport.ai. Получение жалобы мы подтверждаем в течение 5 рабочих дней, а о результате сообщаем не позднее чем в течение 8 дней. Процедура рассмотрения жалоб конфиденциальна.",
          },
          {
            type: "p",
            text: "В соответствии со статьёй 32 Закона о внесудебном разрешении потребительских споров (ZIsRPS) сообщаем, что поставщик не признаёт ни одного исполнителя внесудебного разрешения потребительских споров компетентным для рассмотрения потребительского спора. Это не затрагивает право потребителя обратиться в суд или к компетентным органам.",
          },
          {
            type: "p",
            text: "Европейская платформа онлайн-разрешения споров прекратила работу 20 июля 2025 года на основании Регламента (ЕС) 2024/3228, поэтому ссылку на неё мы больше не публикуем.",
          },
          {
            type: "p",
            text: "Потребитель может также обратиться в Рыночную инспекцию Республики Словении, Dunajska cesta 160, 1000 Ljubljana, либо в Европейский потребительский центр Словении по трансграничным спорам в пределах ЕС.",
          },
        ],
      },
      {
        title: "18. Изменения условий и услуги",
        blocks: [
          {
            type: "p",
            text: "Мы можем изменять настоящие условия в связи с изменением законодательства, изменением услуги или для устранения неясностей. Действующая редакция всегда публикуется на этой странице с датой последнего обновления.",
          },
          {
            type: "p",
            text: "О существенных изменениях мы уведомляем зарегистрированных пользователей по электронной почте не менее чем за 15 дней до вступления в силу. Если вы не согласны с изменением, вы можете прекратить пользование сервисом до его вступления в силу и потребовать удаления учётной записи; в этом случае мы возвращаем сумму, уплаченную за неиспользованные кредиты.",
          },
          {
            type: "p",
            text: "К уже оформленным заказам применяются условия, действовавшие на момент оформления заказа.",
          },
        ],
      },
      {
        title: "19. Применимое право и подсудность",
        blocks: [
          {
            type: "p",
            text: "К договорным отношениям по настоящим условиям применяется право Республики Словении без применения коллизионных норм. Конвенция ООН о договорах международной купли-продажи товаров не применяется.",
          },
          {
            type: "p",
            text: "Споры подсудны компетентному суду Республики Словении. В отношении потребителей такой выбор не лишает их защиты, предоставляемой императивными нормами страны их обычного места жительства; потребитель вправе также подать иск в суд страны своего проживания.",
          },
        ],
      },
      {
        title: "20. Заключительные положения",
        blocks: [
          {
            type: "p",
            text: "Если отдельное положение настоящих условий недействительно или неисполнимо, это не влияет на действительность остальных положений; недействительное положение заменяется действительным, наиболее близким по экономическому смыслу.",
          },
          {
            type: "p",
            text: "Условия публикуются на словенском, английском, немецком и русском языках. При расхождении между языковыми версиями преимущественную силу имеет словенская версия.",
          },
          {
            type: "p",
            text: "По вопросам, связанным с настоящими условиями, пишите на info@nreport.ai.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  privacy: {
    eyebrow: legalEyebrow,
    title: "Политика конфиденциальности",
    updated,
    intro:
      "Настоящая политика разъясняет, какие персональные данные обрабатывает компания Noventis d.o.o. при управлении сайтом nreport.ai и оказании услуги nReport, для каких целей, на каком правовом основании, кому они передаются, как долго хранятся и какие права у вас есть. Она подготовлена в соответствии с Регламентом (ЕС) 2016/679 (GDPR) и Законом о защите персональных данных (ZVOP-2).",
    sections: [
      {
        title: "1. Оператор и контактные данные",
        blocks: [
          { type: "p", text: "Оператором персональных данных является:" },
          { type: "company" },
          {
            type: "p",
            text: "По всем вопросам защиты персональных данных и для реализации прав пишите на privacy@nreport.ai либо на адрес юридического лица.",
          },
          {
            type: "p",
            text: "С учётом характера, объёма и целей обработки оператор не обязан назначать должностное лицо по защите данных (статья 37 GDPR). Если это изменится, контактные данные будут опубликованы здесь.",
          },
        ],
      },
      {
        title: "2. Какие данные мы обрабатываем",
        blocks: [
          {
            type: "table",
            head: ["Категория", "Конкретные данные", "Источник"],
            rows: [
              [
                "Данные учётной записи",
                "адрес электронной почты, имя (если указано), пароль в виде хеша, идентификатор поставщика входа (Google, Facebook), дата регистрации, язык",
                "от вас или от поставщика входа",
              ],
              [
                "Данные заказа",
                "адрес и идентификация запрашиваемого объекта, номер квартиры, тип недвижимости, подготовленные отчёты, баланс кредитов, история использования, время первого открытия отчёта, скачивания PDF и просмотра переговорного листа",
                "от вас и из работы сервиса",
              ],
              [
                "Платёжные данные",
                "сумма, валюта, время, статус платежа, идентификатор сессии у поставщика платежей, реквизиты счёта. Данные платёжной карты мы не получаем и не храним.",
                "от поставщика платёжных услуг Stripe",
              ],
              [
                "Технические данные",
                "IP-адрес, сведения о браузере и устройстве, время доступа, запрошенные URL, журналы ошибок",
                "автоматически при использовании",
              ],
              [
                "Записи о согласиях",
                "ваш выбор в отношении файлов cookie и его время, согласие на рассылку, согласие на немедленное исполнение при покупке",
                "от вас",
              ],
              [
                "Переписка",
                "содержание направленных нам сообщений и наши ответы",
                "от вас",
              ],
              [
                "Данные об использовании и маркетинге",
                "события в приложении, источники трафика, конверсии — только в объёме, разрешённом вашим согласием на файлы cookie",
                "автоматически, при наличии согласия",
              ],
            ],
          },
          {
            type: "p",
            text: "Особые категории персональных данных по статье 9 GDPR (например, о здоровье, вероисповедании или политических взглядах) мы не собираем и просим не направлять их нам.",
          },
        ],
      },
      {
        title: "3. Цели и правовые основания обработки",
        blocks: [
          {
            type: "table",
            head: ["Цель", "Правовое основание", "Срок хранения"],
            rows: [
              [
                "Ведение учётной записи и вход в систему",
                "исполнение договора — ст. 6(1)(b) GDPR",
                "до удаления учётной записи; далее до 30 дней в резервных копиях",
              ],
              [
                "Подготовка и предоставление заказанного отчёта",
                "исполнение договора — ст. 6(1)(b) GDPR",
                "в течение срока существования учётной записи, чтобы отчёты оставались доступны",
              ],
              [
                "Обработка платежей и предотвращение мошенничества",
                "исполнение договора — ст. 6(1)(b); правовая обязанность — ст. 6(1)(c); законный интерес — ст. 6(1)(f)",
                "по правилам хранения бухгалтерской документации",
              ],
              [
                "Выставление и хранение счетов, налоговые обязанности",
                "правовая обязанность — ст. 6(1)(c) GDPR (ZDDV-1, ZGD-1)",
                "10 лет с конца года, к которому относится счёт",
              ],
              [
                "Транзакционные уведомления (подтверждение заказа, готовность отчёта, сброс пароля)",
                "исполнение договора — ст. 6(1)(b) GDPR",
                "до удаления учётной записи",
              ],
              [
                "Поддержка пользователей и рассмотрение жалоб",
                "исполнение договора — ст. 6(1)(b); законный интерес — ст. 6(1)(f)",
                "3 года после завершения вопроса",
              ],
              [
                "Безопасность системы, журналы, предотвращение злоупотреблений",
                "законный интерес — ст. 6(1)(f) GDPR",
                "как правило 90 дней; дольше только при расследовании конкретного инцидента",
              ],
              [
                "Измерение посещаемости и улучшение сервиса (аналитика)",
                "согласие — ст. 6(1)(a) GDPR и ст. 225 ZEKom-2",
                "до отзыва согласия; файлы cookie — по срокам Политики файлов cookie",
              ],
              [
                "Реклама и измерение её эффективности",
                "согласие — ст. 6(1)(a) GDPR и ст. 225 ZEKom-2",
                "до отзыва согласия",
              ],
              [
                "Рассылка новостей",
                "согласие — ст. 6(1)(a) GDPR",
                "до отписки или отзыва согласия",
              ],
              [
                "Заявление или защита правовых требований",
                "законный интерес — ст. 6(1)(f) GDPR",
                "до истечения сроков исковой давности",
              ],
            ],
          },
          {
            type: "p",
            text: "Там, где мы опираемся на законный интерес, проведено взвешивание этого интереса и ваших прав. Краткое изложение предоставим по запросу на privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "4. Данные об объекте и данные третьих лиц",
        blocks: [
          {
            type: "p",
            text: "Адрес запрашиваемого объекта для нас является данными заказа. Если объект связан с вами (например, ваше место жительства), он одновременно является вашими персональными данными и обрабатывается так же, как прочие данные заказа.",
          },
          {
            type: "p",
            text: "Отчёт может содержать данные публичных реестров, относящиеся к третьим лицам, — в частности, если для объекта доступна выписка из поземельной книги со сведениями о собственности или обременениях. Эти данные мы обрабатываем исключительно для подготовки заказанного вами отчёта на основании законного интереса по статье 6(1)(f) GDPR и только в том объёме, в котором они уже общедоступны в реестре.",
          },
          {
            type: "p",
            text: "Мы не формируем на их основе профили лиц, не ведём отдельную базу данных собственников и не используем их для маркетинга. Поскольку данные получены не напрямую от субъекта и индивидуальное уведомление потребовало бы несоразмерных усилий, мы опираемся на исключение статьи 14(5)(b) GDPR и вместо индивидуального уведомления публикуем настоящую политику.",
          },
          {
            type: "note",
            text: "Как заказчик вы отвечаете за наличие законного интереса в отношении запрашиваемого объекта (например, планируемая покупка, аренда, финансирование, оценка или право собственности). Сервис нельзя использовать для слежки, домогательств или профилирования лиц.",
          },
        ],
      },
      {
        title: "5. Источники, не являющиеся персональными данными пользователя",
        blocks: [
          {
            type: "p",
            text: "Для подготовки отчётов мы используем общедоступные источники: реестры Геодезического управления Республики Словении (кадастр недвижимости, реестр недвижимости, Реестр рынка недвижимости), поземельную книгу, реестр энергетических сертификатов, картографические источники и опубликованные объявления о недвижимости. Эти источники находятся вне нашего контроля, к ним применяются условия их операторов.",
          },
        ],
      },
      {
        title: "6. Файлы cookie и аналогичные технологии",
        blocks: [
          {
            type: "p",
            text: "Необязательные файлы cookie и аналогичные технологии мы не загружаем до получения вашего согласия. При первом посещении мы показываем уведомление, в котором можно принять всё, отказаться или выбрать отдельные категории. Отказ так же прост, как и согласие.",
          },
          {
            type: "p",
            text: "Для входа и работы сервиса используется локальное хранилище браузера; это строго необходимо для оказания прямо запрошенной вами услуги, поэтому согласие не требуется (часть 2 статьи 225 ZEKom-2).",
          },
          { type: "p", text: "Подробный перечень приведён в Политике файлов cookie." },
          { type: "cookieSettings", label: "Настройки файлов cookie" },
        ],
      },
      {
        title: "7. Кому мы передаём данные",
        blocks: [
          {
            type: "p",
            text: "Мы не продаём персональные данные. Мы передаём их только обработчикам, действующим по нашим указаниям на основании договора об обработке (статья 28 GDPR), а также органам власти, когда это требуется по закону.",
          },
          {
            type: "table",
            head: ["Получатель", "Цель", "Место обработки", "Основание передачи"],
            rows: [
              [
                "Supabase, Inc.",
                "база данных, вход пользователей, файловое хранилище, серверные функции",
                "ЕС (Франкфурт, eu-central-1); компания в США",
                "договор об обработке и стандартные договорные положения",
              ],
              [
                "Railway Corp.",
                "хостинг серверной части, очереди задач и формирование PDF",
                "США / ЕС",
                "стандартные договорные положения",
              ],
              [
                "Vercel Inc.",
                "хостинг и доставка сайта",
                "ЕС / США",
                "стандартные договорные положения, EU-US Data Privacy Framework",
              ],
              [
                "Cloudflare, Inc.",
                "сетевой посредник, защита от злоупотреблений и атак",
                "глобальная сеть",
                "стандартные договорные положения, EU-US Data Privacy Framework",
              ],
              [
                "Stripe Payments Europe, Ltd.",
                "проведение платежей, предотвращение мошенничества (для этой цели — самостоятельный оператор)",
                "Ирландия, США",
                "стандартные договорные положения",
              ],
              [
                "Anthropic, PBC",
                "генерация текстовых частей отчёта языковой моделью",
                "США",
                "стандартные договорные положения; данные не используются для обучения моделей",
              ],
              [
                "Google Ireland Ltd. / Google LLC",
                "карты, поиск адресов и геокодирование; языковая модель Gemini; Google Analytics (только при согласии)",
                "Ирландия, США",
                "стандартные договорные положения, EU-US Data Privacy Framework",
              ],
              [
                "Meta Platforms Ireland Ltd.",
                "измерение эффективности рекламы (только при маркетинговом согласии)",
                "Ирландия, США",
                "стандартные договорные положения, EU-US Data Privacy Framework",
              ],
              [
                "Resend, Inc.",
                "отправка транзакционных писем",
                "США",
                "стандартные договорные положения",
              ],
              [
                "MailerLite Limited",
                "рассылка новостей (только при согласии)",
                "Европейский союз",
                "передача в третьи страны отсутствует",
              ],
            ],
          },
          {
            type: "p",
            text: "Мы также можем раскрыть данные суду, налоговому или надзорному органу, полиции или иному компетентному органу, когда обязаны это сделать, а также нашему бухгалтеру, адвокату или аудитору в необходимом для их работы объёме. При реорганизации или продаже бизнеса данные могут перейти к правопреемнику, о чём мы уведомим заранее.",
          },
        ],
      },
      {
        title: "8. Передача в третьи страны",
        blocks: [
          {
            type: "p",
            text: "Некоторые обработчики зарегистрированы или имеют подразделения поддержки в Соединённых Штатах Америки. В таких случаях передача основывается на решении Европейской комиссии об адекватности для EU-US Data Privacy Framework (если получатель сертифицирован) либо на стандартных договорных положениях Комиссии, дополненных оценкой воздействия передачи и дополнительными техническими мерами, такими как шифрование при передаче и хранении и ограничение доступа.",
          },
          {
            type: "p",
            text: "Копию применяемых гарантий можно запросить на privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "9. Автоматизированное принятие решений и профилирование",
        blocks: [
          {
            type: "p",
            text: "Отчёт, включая ориентировочную оценку стоимости объекта, формируется автоматически без проверки человеком. Такая обработка касается объекта недвижимости и не порождает правовых последствий для физического лица и не влияет на него сходным существенным образом, поэтому не является автоматизированным принятием решений по смыслу части 1 статьи 22 GDPR.",
          },
          {
            type: "p",
            text: "Мы не используем ваши персональные данные для автоматизированной оценки кредитоспособности, риска или аналогичного ранжирования лиц.",
          },
        ],
      },
      {
        title: "10. Безопасность обработки",
        blocks: [
          {
            type: "ul",
            items: [
              "шифрование при передаче (TLS) и шифрование хранимых данных,",
              "ограничение доступа по принципу минимально необходимых прав и отдельные административные доступы,",
              "безопасность на уровне строк в базе данных, так что пользователь получает доступ только к своим записям,",
              "журналирование административных действий,",
              "регулярные резервные копии и проверка их восстанавливаемости,",
              "договоры об обработке и проверка обработчиков.",
            ],
          },
          {
            type: "p",
            text: "При нарушении защиты персональных данных, которое может повлечь высокий риск для ваших прав и свобод, мы уведомим вас без неоправданной задержки, а о нарушении сообщим Уполномоченному по информации в течение 72 часов.",
          },
        ],
      },
      {
        title: "11. Ваши права",
        blocks: [
          { type: "p", text: "В отношении своих персональных данных вы имеете право на:" },
          {
            type: "ul",
            items: [
              "доступ к данным и получение их копии (статья 15 GDPR),",
              "исправление неточных и дополнение неполных данных (статья 16),",
              "удаление данных, «право быть забытым» (статья 17),",
              "ограничение обработки (статья 18),",
              "переносимость данных в машиночитаемом формате (статья 20),",
              "возражение против обработки, основанной на законном интересе, и возражение в любое время против обработки в целях прямого маркетинга (статья 21),",
              "отзыв согласия в любое время без влияния на законность обработки до отзыва (статья 7).",
            ],
          },
          {
            type: "p",
            text: "Запросы направляйте на privacy@nreport.ai. Мы отвечаем не позднее одного месяца с момента получения; при сложном запросе срок может быть продлён на два месяца, о чём мы сообщим. Реализация прав бесплатна; при явно необоснованных или чрезмерных запросах мы вправе взимать разумную плату либо отказать. В целях безопасности мы можем запросить дополнительные сведения для подтверждения вашей личности.",
          },
          {
            type: "p",
            text: "Удаление учётной записи и данных можно также запросить через специальную форму на сайте. Часть данных мы обязаны хранить несмотря на запрос об удалении, если этого требует закон, — в частности выставленные счета в течение налогового срока хранения.",
          },
          {
            type: "p",
            text: "Если вы считаете, что обработка ваших персональных данных нарушает законодательство, вы вправе подать жалобу в надзорный орган:",
          },
          {
            type: "links",
            items: [
              {
                label:
                  "Уполномоченный по информации Республики Словении, Dunajska cesta 22, 1000 Ljubljana — www.ip-rs.si",
                href: "https://www.ip-rs.si/",
              },
            ],
          },
          {
            type: "p",
            text: "Жалобу можно подать также в надзорный орган страны вашего обычного места жительства или места предполагаемого нарушения.",
          },
        ],
      },
      {
        title: "12. Обязательно ли предоставление данных",
        blocks: [
          {
            type: "p",
            text: "Предоставление адреса электронной почты и данных об объекте является условием заключения и исполнения договора — без них мы не можем подготовить и передать отчёт. Предоставление данных для рассылки и согласие на необязательные файлы cookie полностью добровольны; отказ от них не влияет на использование сервиса.",
          },
        ],
      },
      {
        title: "13. Дети",
        blocks: [
          {
            type: "p",
            text: "Сервис не предназначен для лиц младше 18 лет, и мы сознательно не собираем их данные. Если мы обнаружим, что обработали данные несовершеннолетнего без надлежащего основания, мы их удалим. Если вы полагаете, что это произошло, сообщите нам на privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "14. Ссылки на другие сайты",
        blocks: [
          {
            type: "p",
            text: "Сайт содержит ссылки на страницы третьих лиц (например, официальные реестры или площадки объявлений). Мы не отвечаем за их содержание и обращение с персональными данными; рекомендуем ознакомиться с их политиками конфиденциальности.",
          },
        ],
      },
      {
        title: "15. Изменения политики",
        blocks: [
          {
            type: "p",
            text: "Мы можем обновлять политику при изменении сервиса, обработчиков или законодательства. Каждая версия содержит дату последнего обновления. О существенных изменениях мы уведомляем зарегистрированных пользователей по электронной почте или уведомлением на сайте; если изменение требует вашего согласия, мы получим его отдельно.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  cookies: {
    eyebrow: legalEyebrow,
    title: "Политика файлов cookie",
    updated,
    intro:
      "Настоящая политика разъясняет, какие файлы cookie и аналогичные технологии использует сайт nreport.ai, управляемый компанией Noventis d.o.o., зачем они применяются и как в любой момент изменить свой выбор.",
    sections: [
      {
        title: "1. Что такое файлы cookie",
        blocks: [
          {
            type: "p",
            text: "Файлы cookie — небольшие текстовые файлы, которые сайт сохраняет в вашем браузере. Схожую роль играют локальное хранилище, хранилище сессии и пиксели отслеживания. В настоящей политике все они совместно именуются «файлы cookie».",
          },
          {
            type: "p",
            text: "Файлы cookie позволяют сайту запоминать ваш вход и настройки; некоторые из них также используются для измерения посещаемости и для рекламы.",
          },
        ],
      },
      {
        title: "2. Правовое основание и ваше согласие",
        blocks: [
          {
            type: "p",
            text: "Согласно части 1 статьи 225 Закона об электронных коммуникациях (ZEKom-2) и статье 6(1)(a) GDPR мы не сохраняем и не считываем необязательные файлы cookie до получения вашего согласия. Согласие является добровольным, конкретным и недвусмысленным, а отказ так же прост, как и согласие.",
          },
          {
            type: "p",
            text: "Строго необходимые файлы cookie не требуют согласия, поскольку необходимы для оказания прямо запрошенной вами услуги (часть 2 статьи 225 ZEKom-2).",
          },
          {
            type: "p",
            text: "Согласие можно отозвать или изменить в любой момент. Отзыв не влияет на законность обработки, осуществлённой до отзыва.",
          },
          { type: "cookieSettings", label: "Настройки файлов cookie" },
        ],
      },
      {
        title: "3. Используемые категории файлов cookie",
        subsections: [
          {
            title: "Строго необходимые (всегда активны)",
            blocks: [
              {
                type: "p",
                text: "Обеспечивают вход, сохранение сессии, безопасность форм и проведение платежа. Без них сайт не работает. Согласия они не требуют и не могут быть отключены.",
              },
              {
                type: "table",
                head: ["Название / технология", "Поставщик", "Назначение", "Срок"],
                rows: [
                  [
                    "sb-…-auth-token (локальное хранилище)",
                    "nReport / Supabase",
                    "сохранение входа пользователя",
                    "до выхода из системы или очистки данных браузера",
                  ],
                  [
                    "nreport-cookie-consent (локальное хранилище)",
                    "nReport",
                    "хранение вашего выбора в отношении файлов cookie",
                    "до отзыва или очистки данных браузера",
                  ],
                  [
                    "selectedAddress, selectedPropertyType и подобные (локальное хранилище)",
                    "nReport",
                    "сохранение введённых данных между шагами заказа",
                    "до завершения заказа",
                  ],
                  [
                    "metaPurchaseContext (хранилище сессии)",
                    "nReport",
                    "передача данных о покупке на страницу подтверждения",
                    "до закрытия вкладки",
                  ],
                  [
                    "__stripe_mid, __stripe_sid",
                    "Stripe",
                    "проведение платежа и предотвращение мошенничества; устанавливаются только на странице оплаты",
                    "1 год / 30 минут",
                  ],
                ],
              },
            ],
          },
          {
            title: "Аналитические (только с вашего согласия)",
            blocks: [
              {
                type: "p",
                text: "Помогают понять, сколько посетителей у сайта, какие страницы они просматривают и где возникают трудности. Данные используем для улучшения сервиса.",
              },
              {
                type: "table",
                head: ["Название", "Поставщик", "Назначение", "Срок"],
                rows: [
                  [
                    "_ga, _ga_*",
                    "Google Analytics 4 (Google Ireland Ltd.)",
                    "различение посетителей и измерение посещаемости",
                    "до 2 лет",
                  ],
                  [
                    "_gid, _gat",
                    "Google Analytics 4",
                    "различение посетителей, ограничение частоты запросов",
                    "24 часа / 1 минута",
                  ],
                ],
              },
              {
                type: "p",
                text: "Google Analytics используется в режиме Consent Mode; без вашего согласия скрипт вообще не загружается, а Google Signals и персонализация рекламы отключены.",
              },
            ],
          },
          {
            title: "Маркетинговые (только с вашего согласия)",
            blocks: [
              {
                type: "p",
                text: "Позволяют измерять эффективность наших рекламных кампаний и показывать более релевантную рекламу на платформах Meta.",
              },
              {
                type: "table",
                head: ["Название", "Поставщик", "Назначение", "Срок"],
                rows: [
                  [
                    "_fbp",
                    "Meta Platforms Ireland Ltd.",
                    "распознавание браузера для измерения конверсий",
                    "до 3 месяцев",
                  ],
                  [
                    "_fbc",
                    "Meta Platforms Ireland Ltd.",
                    "фиксация клика по рекламе, с которой вы пришли на сайт",
                    "до 3 месяцев",
                  ],
                ],
              },
              {
                type: "p",
                text: "Если вы не разрешаете маркетинговые файлы cookie, мы не загружаем пиксель Meta и не сообщаем о вашей покупке в Meta со стороны сервера.",
              },
            ],
          },
        ],
      },
      {
        title: "4. Как изменить или отозвать согласие",
        blocks: [
          {
            type: "p",
            text: "Изменить выбор можно в любой момент кнопкой ниже или по ссылке «Настройки файлов cookie» в нижней части сайта.",
          },
          { type: "cookieSettings", label: "Настройки файлов cookie" },
          {
            type: "p",
            text: "При отзыве согласия мы прекращаем загрузку соответствующих скриптов и удаляем те файлы cookie, которые можем удалить. Уже сохранённые в браузере файлы cookie вы можете удалить самостоятельно в настройках браузера.",
          },
        ],
      },
      {
        title: "5. Управление файлами cookie в браузере",
        blocks: [
          {
            type: "ul",
            items: [
              "Chrome: Настройки › Конфиденциальность и безопасность › Сторонние файлы cookie",
              "Firefox: Настройки › Приватность и защита › Куки и данные сайтов",
              "Safari: Настройки › Конфиденциальность › Управление данными веб-сайтов",
              "Edge: Настройки › Файлы cookie и разрешения сайтов",
            ],
          },
          {
            type: "p",
            text: "Блокировка всех файлов cookie может сделать невозможными вход и покупку, поскольку сайт не сможет поддерживать вашу сессию.",
          },
        ],
      },
      {
        title: "6. Политики сторонних поставщиков",
        blocks: [
          {
            type: "links",
            items: [
              {
                label: "Google — Политика конфиденциальности",
                href: "https://policies.google.com/privacy",
              },
              {
                label: "Meta — Политика конфиденциальности",
                href: "https://www.facebook.com/privacy/policy",
              },
              { label: "Stripe — Политика конфиденциальности", href: "https://stripe.com/privacy" },
              {
                label: "Supabase — Политика конфиденциальности",
                href: "https://supabase.com/privacy",
              },
            ],
          },
        ],
      },
      {
        title: "7. Изменения и контакты",
        blocks: [
          {
            type: "p",
            text: "Мы обновляем политику файлов cookie при изменении используемых технологий. Если набор файлов cookie существенно меняется, мы запрашиваем ваше согласие заново.",
          },
          {
            type: "p",
            text: "По вопросам о файлах cookie пишите на privacy@nreport.ai. Подробнее об обработке персональных данных — в Политике конфиденциальности.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  dataDeletion: {
    title: "Удаление персональных данных",
    subtitle:
      "Запросите удаление своих персональных данных в соответствии со статьёй 17 Общего регламента по защите данных (GDPR).",
    alert:
      "Важно: удаление необратимо. После удаления вы больше не сможете получить доступ к учётной записи, приобретённым кредитам и подготовленным отчётам.",
    formTitle: "Запрос на удаление данных",
    formDesc: "Заполните форму ниже, чтобы запросить удаление персональных данных.",
    emailLabel: "Адрес электронной почты *",
    emailPlaceholder: "vash.email@primer.ru",
    emailHint: "Укажите адрес электронной почты, связанный с вашей учётной записью.",
    reasonLabel: "Причина удаления (необязательно)",
    reasonPlaceholder: "Опишите причину удаления данных...",
    deletedDataTitle: "Данные, которые будут удалены:",
    deletedDataItems: [
      "Данные учётной записи (эл. почта, имя, настройки)",
      "История заказов и расход кредитов",
      "Сохранённые отчёты и анализы",
      "Загруженные файлы и вложения",
      "Записи о согласиях и предпочтения",
    ],
    submit: "Отправить запрос на удаление",
    contact: "Есть вопросы? Свяжитесь с нами по адресу",
    successTitle: "Запрос успешно отправлен",
    successDesc:
      "Мы получили ваш запрос и обработаем его не позднее одного месяца с момента получения, как того требует статья 12 GDPR.",
    processingTitle: "Срок обработки",
    processingDesc:
      "Запросы обрабатываются не позднее одного месяца. Выставленные счета мы обязаны хранить 10 лет по налоговому законодательству, поэтому они остаются сохранёнными и после удаления учётной записи.",
    securityTitle: "Безопасность данных",
    securityDesc:
      "Перед удалением мы можем запросить подтверждение вашей личности. Данные надёжно удаляются из наших систем, а из резервных копий — не позднее чем через 30 дней.",
    backHome: "Вернуться на главную",
    toastEmailRequired: "Пожалуйста, укажите свой адрес электронной почты.",
    toastSendFailed: "Не удалось отправить запрос. Повторите попытку или напишите на info@nreport.ai.",
    toastSuccessTitle: "Запрос отправлен",
    toastSuccessDesc: "Ваш запрос на удаление данных успешно отправлен.",
  },
};
